import type { ApiErrorResponse, GenerationModelAttempt, GenerationProcessStage, GenerationProgressState, PipelineStage, RepairAppendix, StageStreamChunk } from '../types';
import { apiFetch, createAuthHeaders, ensureAuthToken } from './auth';
import type { TenantQuotaInfo } from './admin';
import type { ChatCatalog } from '../stores/chatCatalogStore';
import { extractFallbackPlcCode } from '../utils/structuredResponse';
import { historyForRequest } from '../utils/requestHistory';

// API client - handles both streaming and non-streaming
const API_BASE = import.meta.env.VITE_API_URL || '';
const STREAM_IDLE_TIMEOUT_MS = 35000; // Must exceed plan review wait (30s) + heartbeat interval (5s)
const STREAM_STATUS_RECHECK_DELAY_MS = 1500;
/**
 * How long recovery will keep asking about a job that keeps reporting itself
 * alive. Generous on purpose: a full project turn has run past ten minutes, and
 * the failure this guards against is a browser stuck on a turn the server
 * already finished. Only a job that never reaches a terminal state hits this.
 */
const STREAM_RECOVERY_DEADLINE_MS = 15 * 60_000;
/** Statuses that mean the worker still has this job in hand. */
const LIVE_JOB_STATUSES = new Set(['processing', 'queued', 'active', 'waiting', 'delayed']);

type StreamChunk = {
  type?: StageStreamChunk['type'];
  text?: string;
  thinking?: string;
  reasoning?: string;
  stage?: GenerationProcessStage | PipelineStage;
  title?: string;
  message?: string;
  delta?: string;
  artifact?: unknown;
  timestamp?: number;
  thought?: string;
  explanation?: string;
  progress?: number;
  progressState?: GenerationProgressState;
  status?: string;
  done?: boolean;
  error?: string;
  replaceContent?: boolean;
  /** The ID the gateway stored the turn under, where it is not the one the turn was sent with. */
  serverSessionId?: string;
  metadata?: {
    deliveryQuality?: 'pending' | 'passed' | 'failed' | 'incomplete';
    provider?: string;
    model?: string;
    jobId?: string | null;
    requestId?: string | null;
    traceId?: string | null;
    recoveredFromStatus?: boolean;
    attempts?: GenerationModelAttempt[];
    timings?: {
      gatewayAcceptedAt?: number;
      queueEnqueuedAt?: number;
      gatewaySetupMs?: number;
      queueWaitMs?: number;
      requestToWorkerStartMs?: number;
      workerStartedAt?: number;
      workerElapsedMs?: number;
      workerTotalMs?: number;
      totalEndToEndMs?: number;
      modelFirstTokenMs?: number;
      modelLatencyMs?: number;
      topicClassifyMs?: number;
      validationMs?: number;
      repairMs?: number;
    };
    clarificationChecklist?: {
      id: string;
      question: string;
      category: string;
      options?: string[];
      multiSelect?: boolean;
      required: boolean;
      fieldKeys?: string[];
      defaultValue?: string;
      rationale?: string;
      autoConfirm?: boolean;
    }[];
    [key: string]: unknown;
  };
  pipeline?: unknown;
  repair?: {
    content?: string;
    reasoning?: string;
    pipeline?: unknown;
    [key: string]: unknown;
  };
  repairAppendix?: RepairAppendix;
  quota?: TenantQuotaInfo;
  offTopic?: boolean;
  clarifyChip?: {
    id: string;
    question: string;
    category: string;
    options?: string[];
    multiSelect?: boolean;
    required: boolean;
    fieldKeys?: string[];
    defaultValue?: string;
    rationale?: string;
    autoConfirm?: boolean;
  };
  chipPending?: { id: string };
  chipCancelled?: { id: string };
  [key: string]: unknown;
};

type StreamRecoveryStatus = {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  error?: string;
  result?: {
    metadata?: StreamChunk['metadata'];
    pipeline?: unknown;
  };
  planReview?: Record<string, unknown>;
};

export function buildRequestContext(config: any) {
  const context: Record<string, unknown> = {};

  if (typeof config?.context === 'string' && config.context.trim()) {
    context.userContext = config.context.trim();
  }

  if (typeof config?.projectId === 'string' && config.projectId.trim()) {
    context.projectId = config.projectId.trim();
  }

  if (typeof config?.scenarioTemplateId === 'string' && config.scenarioTemplateId.trim()) {
    context.scenarioTemplateId = config.scenarioTemplateId.trim();
  }

  if (typeof config?.knowledgeScope === 'string' && config.knowledgeScope.trim()) {
    context.knowledgeScope = config.knowledgeScope.trim();
  }

  const targetProfile = config?.targetProfile && typeof config.targetProfile === 'object'
    ? config.targetProfile
    : undefined;

  if (targetProfile) {
    context.targetProfile = targetProfile;
  }

  // No PLC model: the setting travels as `config.settingsPlcModel`, and the
  // model a turn compiles for is the one the server resolves (TIA 根治 P2, E9).
  for (const key of ['softwareVersion', 'hardwareSeries', 'firmwareVersion']) {
    const value = typeof config?.[key] === 'string' && config[key].trim()
      ? config[key].trim()
      : typeof targetProfile?.[key] === 'string' && targetProfile[key].trim()
        ? targetProfile[key].trim()
        : undefined;
    if (value) {
      context[key] = value;
    }
  }

  if (config?.isClarifyFollowUp === true) {
    context.isClarifyFollowUp = true;
  }

  // Pins the turn to an explanation. Without it the runtime would classify a
  // prompt full of compiler output, and "here are 40 errors" reads a great deal
  // like a request to rewrite the program that produced them.
  if (config?.isValidationExplanation === true) {
    context.isValidationExplanation = true;
  }

  if (config?.clarifyState) {
    const cs = config.clarifyState;
    context.clarifyRound = typeof cs.clarifyRound === 'number' ? cs.clarifyRound : 0;
    if (typeof cs.pinnedProvider === 'string') context.pinnedProvider = cs.pinnedProvider;
    if (typeof cs.pinnedModel === 'string') context.pinnedModel = cs.pinnedModel;
    if (Array.isArray(cs.confirmedAnswers)) {
      context.confirmedAnswers = cs.confirmedAnswers.map((a: any) => ({
        id: a.id,
        category: a.category,
        question: a.question,
        selectedOptions: a.selectedOptions,
        fieldKeys: a.fieldKeys,
      }));
    }
    if (Array.isArray(cs.askedQuestionKeys)) {
      context.askedQuestionKeys = cs.askedQuestionKeys;
    }
    // The turn this submission answers, named the way the server names it.
    // Only the request that submits a card set carries it (see
    // splitClarifySubmitConfig); the gateway claims the card set by it.
    if (typeof cs.answeredRequestId === 'string' && cs.answeredRequestId.trim()) {
      context.answeredRequestId = cs.answeredRequestId.trim();
    }
  }

  // Pass through preApprovedPlan for plan resume flow (timed-out plan continuation)
  if (config?.preApprovedPlan && typeof config.preApprovedPlan === 'object') {
    context.preApprovedPlan = config.preApprovedPlan;
  }

  // V1 P2: Pass RealPLC Agent enabled flag
  if (config?.tiaAgentEnabled === true) {
    context.tiaAgentEnabled = true;
  }

  return Object.keys(context).length ? context : undefined;
}

async function createGenerateRequest(
  prompt: string,
  config: any,
  signal: AbortSignal | undefined,
  history: { role: string; content: string }[] | undefined,
  traceId: string,
  sessionId?: string | null,
) {
  const token = await ensureAuthToken();

  return apiFetch(`${API_BASE}/api/v1/generate/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Trace-Id': traceId,
      ...createAuthHeaders(token),
    },
    body: JSON.stringify({
      prompt,
      sessionId,
      history: historyForRequest(history),
      config,
      context: buildRequestContext(config),
    }),
    signal,
  });
}

export class GenerationRecoveryPendingError extends Error {
  constructor(readonly jobId: string) {
    super('Generation status is unconfirmed. Continue checking the existing job.');
    this.name = 'GenerationRecoveryPendingError';
  }
}
class StatusLookupError extends Error {
  constructor(readonly retryable: boolean, message: string) { super(message); }
}
async function recoveryRequest<T>(operation: (signal: AbortSignal) => Promise<T>, signal?: AbortSignal, timeoutMs = 10000): Promise<T> {
  if (signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let abort: (() => void) | undefined;
  try {
    return await Promise.race([operation(controller.signal), new Promise<never>((_, reject) => {
      timer = setTimeout(() => { reject(new StatusLookupError(true, 'Job status lookup timed out')); controller.abort(); }, timeoutMs);
      abort = () => { reject(new DOMException('The operation was aborted.', 'AbortError')); controller.abort(); };
      signal?.addEventListener('abort', abort, { once: true });
    })]);
  } finally {
    clearTimeout(timer);
    if (abort) signal?.removeEventListener('abort', abort);
  }
}
async function fetchJobStatus(jobId: string, signal?: AbortSignal, timeoutMs = 10000): Promise<StreamRecoveryStatus> {
  return recoveryRequest(async requestSignal => {
    const response = await apiFetch(`${API_BASE}/api/v1/generate/${encodeURIComponent(jobId)}`, { signal: requestSignal });
    if (!response.ok) throw new StatusLookupError([502, 503, 504].includes(response.status), `Failed to get job status: ${response.status}`);
    const data = await response.json();
    return { status: data.status, progress: data.progress, error: data.error, result: data.result };
  }, signal, timeoutMs);
}

function streamChunkFromRecoveredResult(status: StreamRecoveryStatus): StreamChunk | undefined {
  if (status.status !== 'completed' || !status.result) {
    return undefined;
  }

  if (!status.result.metadata && !status.result.pipeline) {
    return undefined;
  }

  return {
    metadata: status.result.metadata
      ? { ...status.result.metadata, recoveredFromStatus: true }
      : { recoveredFromStatus: true },
    pipeline: status.result.pipeline,
  };
}

function hasStructuredTags(content: string) {
  return /\[\/?(?:START|END)_(?:THOUGHT|CODE|EXPLANATION|VERIFICATION|FLOWCHART|SUMMARY)\]/i.test(content);
}

function buildStructuredFallbackText(data: Record<string, unknown>): string {
  const rawText = typeof data.text === 'string' ? data.text : '';
  const rawCode = typeof data.code === 'string' ? data.code : '';
  const thought = typeof data.thought === 'string' ? data.thought.trim() : '';
  const verification = typeof data.verification === 'string' ? data.verification.trim() : '';
  const explanation = typeof data.explanation === 'string' ? data.explanation.trim() : '';
  const flowchart = typeof data.flowchart === 'string' ? data.flowchart.trim() : '';
  const summary = typeof data.summary === 'string' ? data.summary.trim() : '';
  const fallbackText = rawText.trim();
  // The same judgement the stream parser makes, from the same function. This
  // file kept a private keyword test of its own, and wrapped the *whole* reply
  // in [START_CODE] whenever a keyword appeared anywhere in it.
  const codeText = rawCode.trim() || extractFallbackPlcCode(fallbackText);
  // Code lifted out of a fence is shown as the program; leaving that fence in
  // the explanation as well would print the same lines twice. Only that fence:
  // a diagram or a JSON sample in the same reply is not the program.
  const proseText = codeText && codeText !== fallbackText
    ? fallbackText
      .replace(/```[^\n]*\n([\s\S]*?)```/g, (fence, inner: string) => (inner.trim() === codeText ? '' : fence))
      .trim()
    : fallbackText;
  const explanationText = explanation || (proseText && proseText !== codeText ? proseText : '');

  if (hasStructuredTags(rawText)) {
    return rawText;
  }
  if (!rawText && hasStructuredTags(rawCode)) {
    return rawCode;
  }

  const sections: string[] = [];
  if (thought) {
    sections.push(`[START_THOUGHT]\n${thought}\n[END_THOUGHT]`);
  }
  if (codeText) {
    sections.push(`[START_CODE]\n\`\`\`st\n${codeText}\n\`\`\`\n[END_CODE]`);
  }
  if (verification) {
    sections.push(`[START_VERIFICATION]\n${verification}\n[END_VERIFICATION]`);
  }
  if (flowchart) {
    sections.push(`[START_FLOWCHART]\n${flowchart}\n[END_FLOWCHART]`);
  }
  if (summary) {
    sections.push(`[START_SUMMARY]\n${summary}\n[END_SUMMARY]`);
  }
  if (explanationText && !flowchart && !summary) {
    sections.push(`[START_EXPLANATION]\n${explanationText}\n[END_EXPLANATION]`);
  }

  return sections.length > 0 ? sections.join('\n\n') : fallbackText || rawCode;
}

function createTimeoutError(message: string) {
  const error = new Error(message);
  error.name = 'StreamTimeoutError';
  return error;
}

function readApiErrorMessageFromBody(body: Partial<ApiErrorResponse> & { detail?: unknown; retryAfter?: unknown } | null | undefined, fallback: string): string {
  if (!body || typeof body !== 'object') {
    return fallback;
  }

  if (body.error === 'TOKEN_QUOTA_EXHAUSTED' || body.error === 'TOKEN_QUOTA_24H_EXHAUSTED') {
    return String(body.detail || body.message || body.error || fallback);
  }

  if (body.error === 'RATE_LIMIT_EXCEEDED') {
    const retryAfter = typeof body.retryAfter === 'number' ? body.retryAfter : 0;
    const retryHint = retryAfter > 0 ? ` Retry in ${retryAfter}s.` : '';
    return `[API Rate Limit] ${body.message || 'Too many requests'}${retryHint}`;
  }

  const detail = body.message || body.detail || body.error;
  return typeof detail === 'string' && detail.trim() ? detail.trim() : fallback;
}

async function readApiErrorMessage(response: Response, fallback?: string): Promise<string> {
  const defaultMessage = fallback || `HTTP error! status: ${response.status}`;
  try {
    const body = await response.clone().json();
    return readApiErrorMessageFromBody(body, defaultMessage);
  } catch {
    try {
      const text = await response.text();
      return text.trim() || defaultMessage;
    } catch {
      return defaultMessage;
    }
  }
}

async function readWithIdleTimeout(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  signal: AbortSignal | undefined,
  timeoutMs: number
) {
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  let abortHandler: (() => void) | undefined;

  try {
    return await Promise.race([
      reader.read(),
      new Promise<never>((_, reject) => {
        timeoutHandle = setTimeout(() => {
          reject(createTimeoutError(`No stream activity for ${timeoutMs}ms`));
        }, timeoutMs);

        if (signal) {
          abortHandler = () => reject(new DOMException('The operation was aborted.', 'AbortError'));
          signal.addEventListener('abort', abortHandler, { once: true });
        }
      }),
    ]);
  } finally {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
    if (signal && abortHandler) {
      signal.removeEventListener('abort', abortHandler);
    }
  }
}

async function sleep(ms: number, signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
  return await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);

    const onAbort = () => {
      cleanup();
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    };

    const cleanup = () => {
      clearTimeout(timer);
      if (signal) {
        signal.removeEventListener('abort', onAbort);
      }
    };

    if (signal) {
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });
}

async function fetchPlanReview(jobId: string, signal?: AbortSignal): Promise<Record<string, unknown> | undefined> {
  try {
    return await recoveryRequest(async requestSignal => {
      const response = await apiFetch(`${API_BASE}/api/v1/generate/plan-review/${encodeURIComponent(jobId)}`, { signal: requestSignal });
      if (!response.ok) return undefined;
      const data = await response.json();
      return data?.planReview && typeof data.planReview === 'object' ? data.planReview as Record<string, unknown> : undefined;
    }, signal);
  } catch {
    return undefined;
  }
}

/**
 * Rejoin a generation whose connection we lost, rather than watch a still
 * picture until it finishes.
 *
 * `recoverStreamFromJobStatus` below is the older, blunter answer: it asks the
 * job how it is doing every couple of seconds and hands the result over once the
 * whole thing is done. That keeps the turn from being lost, but on 2026-09-08 it
 * meant five and a half minutes of text frozen mid-sentence under a timer that
 * kept counting — the work was streaming the entire time, to nobody.
 *
 * The job is alive, so take the live words. Chunks published while we were away
 * are gone (Redis pub/sub does not replay) and the completion chunk carries the
 * full text regardless, so resuming from now loses nothing that matters.
 *
 * Returns whether the stream reached its terminal chunk. Anything else — a
 * non-OK response, a second drop, an idle reattached stream — leaves the caller
 * to fall back to status recovery.
 */
async function* reattachToJobStream(
  jobId: string,
  signal: AbortSignal | undefined,
): AsyncGenerator<StreamChunk, boolean, unknown> {
  const response = await apiFetch(
    `${API_BASE}/api/v1/generate/${encodeURIComponent(jobId)}/stream`,
    { signal },
  );
  if (!response.ok || !response.body) return false;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let sawTerminalChunk = false;

  while (true) {
    const { done, value } = await readWithIdleTimeout(reader, signal, STREAM_IDLE_TIMEOUT_MS);
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line || line.startsWith(':') || !line.startsWith('data: ')) continue;

      let data: StreamChunk;
      try {
        data = JSON.parse(line.slice(6)) as StreamChunk;
      } catch {
        yield { text: line, done: false };
        continue;
      }

      if (data.error) throw new Error(data.error);
      if (data.done === true) sawTerminalChunk = true;
      // A job that finished while we were away comes back as its stored result
      // in one chunk, and the UI never reads `result`. Hand it over the way
      // status recovery does, or its metadata and pipeline are lost.
      if (data.done === true && data.result) {
        if (data.progress) yield { progress: data.progress };
        const recovered = streamChunkFromRecoveredResult({
          status: 'completed',
          result: data.result as StreamRecoveryStatus['result'],
        });
        if (recovered) yield recovered;
        yield { done: true };
        continue;
      }
      yield data;
    }
  }

  return sawTerminalChunk;
}

/**
 * Reattach if we can, and say whether that finished the turn. A reattach that
 * fails for any reason other than the user cancelling is not an error — it just
 * means the older status recovery takes over, exactly as before.
 */
async function* tryReattach(
  jobId: string | null,
  signal: AbortSignal | undefined,
): AsyncGenerator<StreamChunk, boolean, unknown> {
  if (!jobId) return false;
  try {
    return yield* reattachToJobStream(jobId, signal);
  } catch (error: any) {
    if (error?.name === 'AbortError') throw error;
    console.warn('[stream-reattach] falling back to status recovery', error);
    return false;
  }
}

async function* recoverStreamFromJobStatus(
  jobId: string | null,
  signal?: AbortSignal,
): AsyncGenerator<StreamChunk, StreamRecoveryStatus> {
  if (!jobId) {
    throw createTimeoutError('Stream ended before the job id was available.');
  }

  let consecutiveErrors = 0;

  // Wait for as long as the job keeps saying it is alive, not for a fixed
  // handful of tries.
  //
  // This used to give up after three rechecks — 4.5 seconds. The work it exists
  // to bridge is measured in minutes: requirement analysis alone runs 140-190s.
  // On 2026-09-07 the stream closed at 112s, the job went on running, recovery
  // polled three times, saw `processing`, and threw. The turn completed on the
  // server and the browser sat on "思考中" forever, its counter still climbing.
  // A job that reports progress is a job worth waiting for; the deadline is
  // here only so a job that never terminates cannot hold the UI open.
  const deadline = Date.now() + STREAM_RECOVERY_DEADLINE_MS;

  while (Date.now() < deadline) {
    let status: StreamRecoveryStatus;
    try {
      status = await fetchJobStatus(jobId, signal, Math.max(1, Math.min(10000, deadline - Date.now())));
      consecutiveErrors = 0;
    } catch (error) {
      if (signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
      if (error instanceof StatusLookupError && !error.retryable) throw error;
      yield { metadata: { jobId, recoveryState: 'reconnecting' } };
      const backoffMs = Math.min(8000, 1000 * 2 ** Math.min(consecutiveErrors++, 3));
      await sleep(Math.min(backoffMs, Math.max(1, deadline - Date.now())), signal);
      continue;
    }

    if (status.status === 'completed' || status.status === 'failed') {
      return status;
    }

    // Anything other than a status that means "still working" ends the wait
    // now: an unknown state will not become a terminal one by being asked again.
    if (!LIVE_JOB_STATUSES.has(status.status)) break;

    // If the job is in the plan review phase, try to recover the plan review state
    if (typeof status.progress === 'number' && status.progress >= 18) {
      const planReview = await fetchPlanReview(jobId, signal);
      if (planReview) {
        return { ...status, planReview };
      }
    }

    await sleep(STREAM_STATUS_RECHECK_DELAY_MS, signal);
  }

  throw new GenerationRecoveryPendingError(jobId);
}

export async function checkExistingGeneration(jobId: string, signal?: AbortSignal): Promise<StreamRecoveryStatus> {
  const recovery = recoverStreamFromJobStatus(jobId, signal);
  while (true) {
    const next = await recovery.next();
    if (next.done) return next.value;
  }
}

export async function* generatePlcCodeStream(
  prompt: string,
  config: any,
  signal?: AbortSignal,
  history?: { role: string; content: string }[],
  sessionId?: string | null,
): AsyncGenerator<StreamChunk, void, unknown> {
  const normalizedPrompt = typeof prompt === 'string' ? prompt.trim() : '';
  if (!normalizedPrompt) {
    throw new Error('Missing prompt');
  }

  const traceId = globalThis.crypto?.randomUUID?.() || `trace_${Date.now()}`;
  const response = await createGenerateRequest(normalizedPrompt, config, signal, history, traceId, sessionId);
  let attemptedStreamRead = false;

  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response));
  }

  let activeJobId = response.headers.get('x-job-id') || response.headers.get('X-Job-Id');
  let activeTraceId = response.headers.get('x-trace-id') || response.headers.get('X-Trace-Id') || traceId;
  let activeRequestId = activeJobId;

  // The gateway will not use a conversation ID that is someone else's, or one
  // deleted in another tab or on another device: it stores the turn under one
  // of its own. The page goes on under that one (AppSecure's
  // followServerSession); sending the old one again, every turn after this
  // reached the model with none of the history.
  const storedAs = response.headers.get('x-session-id');
  if (storedAs && sessionId && storedAs !== sessionId) {
    yield { serverSessionId: storedAs };
  }

  // Try streaming
  try {
    const reader = response.body?.getReader();
    if (reader) {
      attemptedStreamRead = true;
      const decoder = new TextDecoder();
      let buffer = '';
      let receivedTerminalChunk = false;

      // Measured rather than guessed.
      //
      // `STREAM_IDLE_TIMEOUT_MS` is a constant nobody has checked against a real
      // generation, and when it fires the user is told the stream stalled — the
      // same sentence the connection merely closing produces, so afterwards the
      // two cannot be told apart. Whether that ceiling is too tight is a
      // question about the largest gap a healthy run actually contains, and
      // these counters are the only place that number exists.
      const streamStartedAt = performance.now();
      let lastReadAt = streamStartedAt;
      let chunkCount = 0;
      let maxGapMs = 0;
      const reportStreamGap = (reason: 'idle-timeout' | 'stream-closed-early') => {
        console.warn('[stream-gap]', {
          reason,
          jobId: activeJobId,
          traceId: activeTraceId,
          chunks: chunkCount,
          maxGapMs: Math.round(maxGapMs),
          finalGapMs: Math.round(performance.now() - lastReadAt),
          idleTimeoutMs: STREAM_IDLE_TIMEOUT_MS,
          streamElapsedMs: Math.round(performance.now() - streamStartedAt),
        });
      };

      while (true) {
        let readResult: ReadableStreamReadResult<Uint8Array>;
        try {
          readResult = await readWithIdleTimeout(reader, signal, STREAM_IDLE_TIMEOUT_MS);
        } catch (error: any) {
          if (error?.name === 'AbortError') {
            throw error;
          }

          reportStreamGap('idle-timeout');
          if (yield* tryReattach(activeJobId, signal)) return;
          const status = yield* recoverStreamFromJobStatus(activeJobId, signal);
          if (status.progress) {
            yield { progress: status.progress };
          }
          if (status.planReview) {
            yield {
              metadata: {
                planReview: status.planReview,
                jobId: activeJobId,
                traceId: activeTraceId,
                requestId: activeRequestId,
              },
            };
          }
          if (status.status === 'failed') {
            throw new Error(status.error || 'Generation failed while recovering stalled stream.');
          }
          const recovered = streamChunkFromRecoveredResult(status);
          if (recovered) {
            yield recovered;
          }
          yield { done: true };
          return;
        }

        const { done, value } = readResult;
        const readAt = performance.now();
        maxGapMs = Math.max(maxGapMs, readAt - lastReadAt);
        lastReadAt = readAt;
        if (!done) chunkCount += 1;

        if (done) {
          if (!receivedTerminalChunk) {
            reportStreamGap('stream-closed-early');
            if (yield* tryReattach(activeJobId, signal)) return;
            const status = yield* recoverStreamFromJobStatus(activeJobId, signal);
            if (status.progress) {
              yield { progress: status.progress };
            }
            if (status.planReview) {
              yield {
                metadata: {
                  planReview: status.planReview,
                  jobId: activeJobId,
                  traceId: activeTraceId,
                  requestId: activeRequestId,
                },
              };
            }
            if (status.status === 'failed') {
              throw new Error(status.error || 'Generation failed after the stream closed unexpectedly.');
            }
            const recovered = streamChunkFromRecoveredResult(status);
            if (recovered) {
              yield recovered;
            }
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line || line.startsWith(':')) {
            continue;
          }

          if (line.startsWith('data: ')) {
            let data: StreamChunk;
            try {
              data = JSON.parse(line.slice(6)) as StreamChunk;
            } catch {
              yield { text: line, done: false };
              continue;
            }

            if (data.metadata) {
              const normalizedMetadata = {
                ...data.metadata,
                jobId: typeof data.metadata.jobId === 'string' ? data.metadata.jobId : (activeJobId || undefined),
                traceId: typeof data.metadata.traceId === 'string' ? data.metadata.traceId : activeTraceId,
                requestId: typeof data.metadata.requestId === 'string'
                  ? data.metadata.requestId
                  : (activeRequestId || activeJobId || undefined),
              };

              activeJobId = normalizedMetadata.jobId || activeJobId;
              activeTraceId = normalizedMetadata.traceId || activeTraceId;
              activeRequestId = normalizedMetadata.requestId || activeRequestId || activeJobId;
              data = {
                ...data,
                metadata: normalizedMetadata,
              };
            }

            if (data.done === true) {
              receivedTerminalChunk = true;
            }
            if (data.error) {
              throw new Error(data.error);
            }
            yield data;
          }
        }
      }
      yield { done: true };
      return;
    }
  } catch (error) {
    if (attemptedStreamRead) {
      throw error;
    }
  }

  // Non-streaming
  const data = await response.json();
  yield {
    text: buildStructuredFallbackText(data),
    thought: data.thought || '',
    explanation: data.explanation || '',
    flowchart: data.flowchart || '',
    summary: data.summary || '',
    done: true,
  };
}

/**
 * Which providers the platform holds a key for.
 *
 * Returns null on any failure, and the selector reads null as "offer
 * everything". A provider the user pasted their own key for must stay
 * selectable even when this call never answers.
 */
/**
 * The providers and models the admin console offers to this user.
 * Returns null on any failure; the store keeps its last successful catalog.
 * Until visibility is known, non-admin selectors only offer pinned entries.
 */
export async function loadModelCatalog(): Promise<ChatCatalog | null> {
  try {
    const token = await ensureAuthToken();
    const response = await apiFetch(`${API_BASE}/api/v1/generate/model-catalog`, {
      headers: createAuthHeaders(token),
    });
    if (!response.ok) return null;
    const payload = await response.json();
    if (!payload || !Array.isArray(payload.providers) || !Array.isArray(payload.models)) return null;
    return {
      configured: Boolean(payload.configured),
      providers: payload.providers,
      models: payload.models,
    };
  } catch {
    return null;
  }
}

export async function loadProviderAvailability(): Promise<Record<string, boolean> | null> {
  try {
    const token = await ensureAuthToken();
    const response = await apiFetch(`${API_BASE}/api/v1/generate/provider-availability`, {
      headers: createAuthHeaders(token),
    });
    if (!response.ok) return null;
    const payload = await response.json();
    const providers = payload?.providers;
    return providers && typeof providers === 'object' ? providers as Record<string, boolean> : null;
  } catch {
    return null;
  }
}

export type ModelConnectionTestResult = {
  ok: boolean;
  provider: string;
  model: string;
  latencyMs?: number;
  message: string;
};

export async function testModelConnection(options: {
  provider: string;
  model: string;
  apiKey?: string;
  baseUrl?: string;
}): Promise<ModelConnectionTestResult> {
  const token = await ensureAuthToken();
  const response = await apiFetch(`${API_BASE}/api/v1/generate/test-connection`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...createAuthHeaders(token),
    },
    body: JSON.stringify(options),
  });

  let payload: any = {};
  try {
    payload = await response.json();
  } catch {
    // Keep the fallback message below.
  }

  if (!response.ok || payload.ok === false) {
    return {
      ok: false,
      provider: options.provider,
      model: options.model,
      message: payload.message || `Connection test failed with status ${response.status}`,
    };
  }

  return {
    ok: true,
    provider: payload.provider || options.provider,
    model: payload.model || options.model,
    latencyMs: payload.latencyMs,
    message: payload.message || 'Connection succeeded',
  };
}

export type ModelDiscoveryResult = {
  ok: boolean;
  message: string;
  models: string[];
};

export async function discoverModelConnectionModels(options: {
  provider: string;
  apiKey?: string;
  baseUrl?: string;
}): Promise<ModelDiscoveryResult> {
  const token = await ensureAuthToken();
  const response = await apiFetch(`${API_BASE}/api/v1/generate/discover-models`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...createAuthHeaders(token),
    },
    body: JSON.stringify(options),
  });

  let payload: any = {};
  try {
    payload = await response.json();
  } catch {
    // Keep the fallback message below.
  }

  if (!response.ok || payload.ok === false) {
    return {
      ok: false,
      message: payload.message || `Model discovery failed with status ${response.status}`,
      models: [],
    };
  }

  return {
    ok: true,
    message: payload.message || 'Models discovered',
    models: Array.isArray(payload.models) ? payload.models : [],
  };
}

export interface RecoveredPlanReview {
  planReview: Record<string, unknown>;
}

export async function recoverPlanReview(jobId: string): Promise<RecoveredPlanReview | null> {
  const trimmedJobId = jobId.trim();
  if (!trimmedJobId) return null;

  const token = await ensureAuthToken();
  const response = await apiFetch(`${API_BASE}/api/v1/generate/plan-review/${encodeURIComponent(trimmedJobId)}`, {
    method: 'GET',
    headers: createAuthHeaders(token),
  });

  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Failed to recover plan review: ${response.status}`);
  }

  const data = await response.json();
  if (data?.planReview) {
    return { planReview: data.planReview };
  }
  return null;
}

export async function cancelPlcGeneration(jobId: string): Promise<void> {
  const trimmedJobId = jobId.trim();
  if (!trimmedJobId) return;

  const token = await ensureAuthToken();
  const response = await apiFetch(`${API_BASE}/api/v1/generate/${encodeURIComponent(trimmedJobId)}/cancel`, {
    method: 'POST',
    headers: createAuthHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`Failed to cancel generation job: ${response.status}`);
  }
}
